import { getOrCreateMainLogger } from "../logger/main";
import { Manifest } from "../types/types";
import { Notification } from "electron";
import fs from "fs";
import path from "path";
import { WebSocketServer, WebSocket } from "ws";
import https from "https";
import http from "http";
const logger = getOrCreateMainLogger();

export type IACOptions = {
	key?: string;
	cert?: string;
	ca?: string;
	secure?: boolean;
};

//export type ISocket = typeof ws.Server | null | any;
interface ISocket {
	name?: string;
	send?: Function;
}

export type IAC_Socket = ISocket | null;

/**
 * Inter-Application Communication (IAC) is a transport that allows Finsemble router communications over a WebSocket.
 * It opens a WebSocket server on the address provided by the manifest under `finsemble.router.transportSettings.FinsembleTransport.serverAddress`.
 * By default it runs under `ws://127.0.0.1:3376.
 *
 * It is possible for browser pages to implement CSP rules that block the websocket using the "connect-src" field.
 *
 * Node.js does not support WebSockets natively so IAC is built with the "ws" module: https://github.com/websockets/ws/blob/HEAD/doc/ws.md#ws
 */
export class IAC {
	wss: WebSocketServer | null;

	port: number;

	host: string;

	options: IACOptions;

	clientSocketMap: Record<string, IAC_Socket> = {};

	blockedClientMap: Record<string, boolean> = {};

	routerServerSocket: IAC_Socket = null;

	/**
	 * Initializes a new instance of the Transport class.
	 *
	 * @param {number} port=3376 The port to run
	 * @param {string} host="127.0.0.1"
	 * @param {object} options
	 * @param {string} options.ca="./IntermediateCA.crt"
	 * @param {boolean} options.secure Set explicitly to false to turn off https
	 */
	constructor(port: number = 3376, host: string = "127.0.0.1", options: IACOptions = {}) {
		this.wss = null;
		this.port = port;
		this.host = host;
		this.options = options;
	}

	/**
	 * Setup a client (based on the initial "start" message addressed to IAC_SERVER). This is where the transport name is mapped to a specific socket.
	 *
	 * @param source transport name the request originated from
	 * @param socket the socket the request arrived on
	 * @returns the socket on success; otherwise null is returned -- this return value is used for unit testing.
	 */
	setupClient(source: string, socket: IAC_Socket): IAC_Socket {
		let setupResult: IAC_Socket = null;
		logger.debug("IAC-Server: setupClient", source);

		if (source === "RouterService") {
			// There can be only one RouterService, which will start very early in the microkernel stage of start (so first one is considered the right one)
			if (!this.routerServerSocket) {
				logger.debug("IAC-Server: RouterService socket identified");
				setupResult = this.routerServerSocket = socket;
			} else {
				logger.error("IAC-Server: Duplicate RouterService socket ignored");
			}
		} else {
			// This check will prevent spoofing at the transport level by ensuring a transport name is never used on more than one socket. It works because all transport names must be unique.
			// Although the RouterService will also filter messages if the origin doesn't match the expected transport, this check provides an extra level of protection beyond
			// what the transport-independent RouterService can do.
			if (!this.blockedClientMap[source] && !this.clientSocketMap[source]) {
				setupResult = this.clientSocketMap[source] = socket;
				logger.debug("IAC-Server: setupClient complete", source);
			} else {
				// if the same transport name is used on more than on socket, it's difficult to know which one is valid, so shutdown all of them.
				// Note there is no normal case where this could happen -- it would require a bad actor trying to reuse a transport nam or an incorrect IAC client implementation.
				logger.error(
					"IAC-Server: multiple socket clients established with the same name so blocking to maintain security."
				);
				this.blockedClientMap[source] = true;
				delete this.clientSocketMap[source];
				const notification = {
					title: "Finsemble-Electron-Adapter",
					body: `SECURITY RISK: A second client with the same transport name tried to connect to Finsemble's web socket server.  The transport ${source} was blocked to maintain security.`,
				};
				new Notification(notification).show();
			}
		}
		return setupResult;
	}

	/**
	 *
	 * To maintain backwards compatibility with clients that don't send an initial "start" message to the IAC_SERVER, here a client is setup based on incoming message when the client isn't known.
	 *
	 * One might wonder why not ONLY use this approach to set up a client (i.e. why bother with the initial "start" message). This approach only works when a message is received
	 * from a client before a message is sent to a client. Therefore, based on current router implementation it will work for most clients (including all components/apps), but it will not work
	 * for the RouterService (which uses the same transport).
	 *
	 * @param source transport name the request originated from
	 * @param socket the socket the request arrived on
	 * @returns the socket on success; otherwise undefined is returned -- this return value is used for unit testing.
	 */
	backwardsCompatabilitysetupClient(source: string, socket: IAC_Socket): IAC_Socket {
		if (!this.blockedClientMap[source] && !this.clientSocketMap[source]) {
			this.clientSocketMap[source] = socket;
			logger.warn(
				`IAC-Server: A deprecated transport client connected to the web socket server.  backwardsCompatabilitysetupClient used for ${source}`
			);
		}
		return this.clientSocketMap[source];
	}

	/**
	 * Remove a socket from the map when it's no longer needed and block any future communication with that client name. Called when a socket closes.
	 * @param socket
	 */
	removeClient(socket: any) {
		const matchingKey = Object.keys(this.clientSocketMap).find((key) => this.clientSocketMap[key] === socket);
		if (matchingKey) {
			// Block removed client from future use so an app can't be killed and its transport spoofed by another client
			this.blockedClientMap[matchingKey] = true;
			delete this.clientSocketMap[matchingKey];
			logger.debug("IAC-Server: removeClient", matchingKey);
		} else {
			logger.warn("IAC-Server: Attempt to remove unidentified socket", socket);
		}
	}

	/**
	 * Sends a message to the routerService, using the router services's socket.
	 *
	 * @param messageString
	 */
	sendMessageToRouterService(messageString: string) {
		logger.debug("IAC-Server: sendMessageToRouterService");
		if (this?.routerServerSocket?.send) {
			this.routerServerSocket.send(messageString);
		} else {
			logger.error(
				"IAC-Server: Client sent message before Router Service connected.  Message discarded.",
				messageString
			);
		}
	}

	/**
	 * Sends a message to a specific client, using the client's socket.
	 * @param client dest to sent message to
	 * @param messageString
	 */
	sendMessageToClient(client: string, messageString: string) {
		logger.debug("IAC-Server: sendMessageToClient", client);
		const socket = this.clientSocketMap[client];
		if (socket?.send) {
			socket.send(messageString);
		}
		// When a native client disconnects without a call to the disconnect API but then reconnects, a NEW entry is added in the
		// clientSocketMap with the new window ID. The previous entry is not removed. But from Finsemble's perspective, these are two
		// different app instances. In this case, Finsemble will not proactively remove the previous instance.
		//
		// Example:
		//  Native app MYAPP creates an instance, INSTANCE_A
		//  INSTANCE_A connects to Finsemble, and is given window ID of 12345
		//  The clientSocketMap looks like
		//   +------------------+--------------+
		//   |       NAME       |   IACSOCKET  |
		//   +------------------+--------------+
		//   | INSTANCE_A-12345 | IAC_SOCKET_1 | // This socket points to INSTANCE_A
		//   +------------------+--------------+
		//
		//  INSTANCE_A disconnects from Finsemble without calling the disconnect method (perhaps due to sleeping or crashing)
		//  The clientSocketMap (still) looks like
		//   +------------------+--------------+
		//   |       NAME       |   IACSOCKET  |
		//   +------------------+--------------+
		//   | INSTANCE_A-12345 | IAC_SOCKET_1 | // This socket points to INSTANCE_A
		//   +------------------+--------------+
		//
		//  INSTANCE_A reconnects to Finsemble, and has a new window ID of 67890
		//  The clientSocketMap now looks like
		//   +------------------+--------------+
		//   |       NAME       |   IACSOCKET  |
		//   +------------------+--------------+
		//   | INSTANCE_A-12345 | IAC_SOCKET_1 | // This socket points to INSTANCE_A
		//   | INSTANCE_A-67890 | IAC_SOCKET_2 | // This socket points to INSTANCE_A
		//   +------------------+--------------+
		//
		//  In this case, Finsemble may still sending heartbeat and possibly other messages
		//  to INSTANCE_A-12345. However, IAC_SOCKET_1 does not exist any longer (even though
		//  the app instance actually remains, but is now assigned a different name and IAC
		//  socket).
		//
		//  Recall that Finsemble is currently unaware that INSTANCE_A-12345 and INSTANCE_A-67890
		//  are the same instance. Further, Finsemble does not remove the INSTANCE_A-12345 record
		//  because Finsemble is optimistic that INSTANCE_A-12345 will reconnect at some point in
		//  the future.
		//
		//  In a perfect world, Finsemble would recognize the reconnect and update the map
		//  accordingly:
		//   +------------------+--------------+
		//   |       NAME       |   IACSOCKET  |
		//   +------------------+--------------+
		//   | INSTANCE_A-12345 | IAC_SOCKET_2 | // NOTE: The previous record has been updated with the new socket
		//   | INSTANCE_A-67890 | IAC_SOCKET_2 |
		//   +------------------+--------------+
		//
		//  However, for Finsemble to be aware of a reconnect, the native client would need to send an in-band
		//  message indicating the previous window ID AND Finsemble would need to update internal records accordingly.
		//  This requires a Java, .NET and TypeScript change.
		//
		//  Or, since this is a no-op currently, simply remove the log message:
		//   else {
		// 	  logger.log("IAC-Server: Message sent to unknown or closed client.  Message discarded.", client, messageString);
		//   }
		//
		//  There may be unintended side effects of updating the map internally; as well, removing the old record at this point might
		//  have unanticipated side effects. By NOT removing the record, the only risk is that clientSocketMap will grow by one entry
		//  for every connection or reconnection, thus consuming a slight amount of memory for each entry (even if the entry is no
		//  longer used). This becomes a problem IFF billions of connections are added to the map; however, in this scenario, it is
		//  anticipated that the system would suffer from other, more substantial failures long before running out of map space.
	}

	/**
	 *  Confirms client message originated on the right router client socket
	 *
	 * @param source name of the source transport
	 * @param socket the socket the message arrived on
	 * @returns true if correct socket
	 */
	isCorrectOriginatingRouterClientSocket(source: string, socket: IAC_Socket): boolean {
		let isCorrectSocket = false;
		if (this.clientSocketMap[source] === socket) {
			isCorrectSocket = true;
		} else {
			logger.error("IAC-Server: Incoming message didn't arrive on expected client socket so message dropped", source);
		}
		return isCorrectSocket;
	}

	/**
	 *  Confirms router service message originated on router service socket
	 *
	 * @param source name of the source transport
	 * @param socket the socket the message arrived on
	 * @returns true if correct socket
	 */
	isCorrectOriginatingRouterServiceSocket(source: string, socket: IAC_Socket): boolean {
		let isCorrectSocket = false;
		if (this.routerServerSocket === socket) {
			isCorrectSocket = true;
		} else {
			logger.error(
				"IAC-Server: Incoming message didn't arrive on expected Router Service socket so message dropped",
				source
			);
		}
		return isCorrectSocket;
	}

	/**
	 * Checks if the source (client name) already exists in the storage.
	 * If yes, updates the socket reference for it.
	 *
	 * @param source name of the source transport
	 * @param socket the socket the message arrived on
	 */
	reconnectNativeClient(source: string, socket: IAC_Socket) {
		logger.debug("IAC-Server: reconnection attempt", source);
		let clientExists = !!this.clientSocketMap[source];

		if (!clientExists && Object.keys(this.blockedClientMap).includes(source)) {
			delete this.blockedClientMap[source];
			clientExists = true;
		}

		if (clientExists) {
			this.clientSocketMap[source] = socket;
		}

		logger.debug(`IAC-Server: reconnection attempt succeed: ${clientExists}`, source);
		socket?.send && socket.send(JSON.stringify({ dest: "TRY_TO_RECONNECT", message: clientExists }));
	}

	/**
	 * Connect the transport to the the web socket and to the router.
	 */
	connect() {
		if (this.options.key && this.options.secure !== false) this.options.secure = true;
		const { port, host, options } = this;
		logger.info(`IAC-Server: Starting on ${host}:${port} secure:${options.secure}`);

		// The ws library requires pre-instantiation of an HTTPS server.
		const server = this.options.secure ? https.createServer(this.options) : http.createServer();
		server.listen(this.port, this.host);
		this.wss = new WebSocketServer({ server });

		this.wss.on("connection", (socket: WebSocket) => {
			socket.on("message", (rawData) => {
				const messageString = rawData.toString();
				// the message must be parsed to access the embedded source, destination, and origin

				let transportMessage;
				try {
					transportMessage = JSON.parse(messageString);
				} catch (exception) {
					logger.error(`IAC-Server: exception thrown parsing message: ${exception}`);
				}

				logger.verbose("IAC-SERVER ONMESSAGE HERE", transportMessage, messageString);
				const { source, dest, message } = transportMessage;

				// Route the message based on it's destination.
				switch (dest) {
					case "ROUTER_SERVICE":
						this.backwardsCompatabilitysetupClient(message.clientMessage.header.origin, socket);
						// If dest is router service then must be from a router client
						if (this.isCorrectOriginatingRouterClientSocket(message.clientMessage.header.origin, socket)) {
							this.sendMessageToRouterService(messageString);
						}
						break;
					case "ROUTER_CLIENT":
						// If dest is a router client then must be from the router service
						if (this.isCorrectOriginatingRouterServiceSocket(message.client, socket)) {
							this.sendMessageToClient(message.client, messageString);
						}
						break;
					case "IAC_KEEPALIVE_SERVER":
						// immediately respond with keep-alive handshake to let client know Finsemble is still up
						this.sendMessageToClient(source, JSON.stringify({ message: "IAC_KeepaliveHandshake" }));
						break;
					case "IAC_SERVER":
						// the only message addressed to the "IAC_SERVER" is the initial "connect" message sent by each IAC client (except the older clients)
						this.setupClient(source, socket);
						break;
					case "TRY_TO_RECONNECT":
						// After socket disconnection the native client will do attempt to reconnect with the old name.
						// It sends a "TRY_TO_RECONNECT" message only once.
						// If FEA storage contains active  or blocked (disconnected) client with the same name, FEA will use it after socket reference update.
						this.reconnectNativeClient(source, socket);
						break;
					default:
						logger.error("IAC-Server: Incoming message has unexpected transport destination", dest);
						break;
				}
			});

			socket.on("close", (code, reason) => {
				logger.debug(`IAC-Server: Received close event with code:${code} and reason:${reason?.toString()}`);
				this.removeClient(socket);
				// When the machine goes to sleep mode RouterService closes the socket connection with code 1006.
				// Sets the routerServerSocket to null to provide reconnection ability.
				if (code === 1006 && socket == this.routerServerSocket) {
					this.routerServerSocket = null;
				}
			});

			socket.on("error", (err: any) => {
				logger.error(`IAC-Server: Received error event ${err}`);
			});
		});
	}
}

/**
 * Starts the IAC.
 */
export const startIAC = (manifest: Manifest): IAC => {
	logger.debug("APPLICATION LIFECYCLE: Starting the IAC");
	const address =
		manifest.finsemble?.router?.transportSettings?.FinsembleTransport?.serverAddress ??
		manifest.finsemble?.IAC?.serverAddress ??
		"";
	if (address === "") {
		logger.warn("Did not find serverAddress for IAC in the manifest.");
	}

	const arr = address.split(":");
	const protocol = arr[0];
	let host = arr[1];
	const port = arr[2];
	let options: any = {};
	// take off the slashed that came from split("wss://host");
	if (host && host.substring(0, 2) == "//") host = host.substring(2);
	const runMode = process.env.ELECTRON_DEV ? "dev" : "installer";

	// If "ws" is explicitly set then force the IAC to run insecure
	if (protocol === "ws") {
		options = { secure: false };
	} else {
		logger.error("wss is no longer supported");
	}
	const iac = new IAC(port, host, options);
	iac.connect();
	return iac;
};
